import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from '@angular/core'
import { DatePipe, NgClass } from '@angular/common'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { catchError, map, of, switchMap } from 'rxjs'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { EventTimingStatus } from '@app/v2/event/enums/event.enum'
import { EventService as PlanningEventService } from '@app/v2/event/services/event.service'
import type { EventTimings } from '@app/v2/event/types/event.type'
import { environment } from '@environments/environment'

type EventTimingsPreviewRow = EventTimings & { start_datetime?: string }

@Component({
  selector: 'app-event-timings-preview',
  templateUrl: './event-timings-preview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, DatePipe],
  providers: [PlanningEventService]
})
export class EventTimingsPreviewComponent {
  private static readonly _defaultVisibleDays = 5

  public readonly eventId = input.required<number>()
  public readonly publicStartDate = input<string>()
  public readonly publicEndDate = input<string>()
  public readonly timezone = input<string>()
  public readonly timezoneOffsetString = input<string>()
  public readonly workspaceSlug = input<string>()
  public readonly eventSlug = input<string>()
  public readonly eventTimingStatus = EventTimingStatus
  public readonly EventTimings = signal<EventTimingsPreviewRow[]>([])
  public readonly isTimingsExpanded = signal(false)
  public readonly displayedTimings = computed(() => {
    const timings = this.EventTimings()
    if (this.isTimingsExpanded() || timings.length <= EventTimingsPreviewComponent._defaultVisibleDays) {
      return timings
    }
    return timings.slice(0, EventTimingsPreviewComponent._defaultVisibleDays)
  })
  public readonly hiddenTimingsCount = computed(() => {
    const remaining = this.EventTimings().length - EventTimingsPreviewComponent._defaultVisibleDays
    return remaining > 0 ? remaining : 0
  })
  public readonly showTimingsToggle = computed(() => this.EventTimings().length > EventTimingsPreviewComponent._defaultVisibleDays)
  private readonly _planningEvent = inject(PlanningEventService)
  private readonly _auth = inject(AuthService)
  private readonly _destroyRef = inject(DestroyRef)

  constructor() {
    this._loadEventTimings()
  }

  readonly addToCalendarUrl = computed((): string | null => {
    if (!this.workspaceSlug() || !this.eventSlug()) {
      return null
    }
    return `${environment.registrationsApi}calender/${this.workspaceSlug()}/${this.eventSlug()}`
  })

  /** e.g. `(GMT +3)` from API offset when set; else IANA at public start (or now). */
  readonly timezoneGmtLabel = computed((): string => {
    const fromApi = this._gmtLabelFromOffsetString(this.timezoneOffsetString())
    if (fromApi) return fromApi

    const tz = this.timezone()?.trim()
    if (!tz) return ''
    const start = this.publicStartDate()
    const instant = start ? new Date(start) : new Date()
    if (!instant?.getTime()) return ''
    return this._ianaToGmtOffsetLabel(tz, instant)
  })

  /**
   * Load the event timings
   */
  private _loadEventTimings(): void {
    toObservable(this.eventId)
      .pipe(
        switchMap((eventId) => {
          if (!eventId || !this._auth.currentWorkspace?.id) {
            this.EventTimings.set([])
            return of(null)
          }
          return this._planningEvent.getEventTimings(this._auth.currentWorkspace?.id, eventId).pipe(
            map((res) => res.data?.timings ?? null),
            catchError(() => of(null))
          )
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((timings) => this._applyEventTimings(timings))
  }

  /**
   * Applies the event timings to the component.
   * @param timings - The event timings.
   */
  private _applyEventTimings(timings: EventTimings[]): void {
    this.isTimingsExpanded.set(false)
    if (!timings?.length) {
      this.EventTimings.set([])
      return
    }
    this.EventTimings.set(
      timings.map((r) => ({
        ...r,
        start_datetime: (r as EventTimingsPreviewRow).start_datetime ?? r.start_date
      }))
    )
  }

  public toggleTimingsExpanded(): void {
    this.isTimingsExpanded.update((expanded) => !expanded)
  }

  /**
   * Inclusive calendar days between two date/datetime strings (YMD portion).
   * @param startYmd - The start date/datetime string
   * @param endYmd - The end date/datetime string
   * @returns The inclusive calendar days between the two dates
   */
  private getInclusiveDaysBetween(startYmd: string, endYmd: string): number {
    const startToken = (startYmd ?? '').slice(0, 10)
    const endToken = (endYmd ?? '').slice(0, 10)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startToken) || !/^\d{4}-\d{2}-\d{2}$/.test(endToken)) {
      return 0
    }

    const [sYear, sMonth, sDay] = startToken.split('-').map(Number)
    const [eYear, eMonth, eDay] = endToken.split('-').map(Number)
    const startUtcMs = Date.UTC(sYear, sMonth - 1, sDay)
    const endUtcMs = Date.UTC(eYear, eMonth - 1, eDay)
    if (Number.isNaN(startUtcMs) || Number.isNaN(endUtcMs) || endUtcMs < startUtcMs) {
      return 0
    }

    const diff = endUtcMs - startUtcMs
    return Math.floor(diff / (24 * 60 * 60 * 1000)) + 1
  }

  /**
   * Build GMT label from API offset (e.g. "+3", "-5", "+02:00").
   * @param offsetValue - The offset value
   * @returns The GMT label
   */
  private _gmtLabelFromOffsetString(offsetValue?: string): string {
    if (!offsetValue) return ''
    return `(GMT ${offsetValue})`
  }

  /**
   * Convert IANA timezone to GMT offset label
   * @param iana - The IANA timezone
   * @param instant - The instant date
   * @returns The GMT offset label
   */
  private _ianaToGmtOffsetLabel(iana: string, instant: Date): string {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: iana,
        timeZoneName: 'shortOffset'
      })
      const raw = formatter.formatToParts(instant).find((p) => p.type === 'timeZoneName')?.value
      if (!raw) return ''
      const tzPart = raw.replace(/\u2212/g, '-')
      const m = tzPart.match(/^GMT([+-])(\d{1,2})(?::(\d{2}))?$/)
      if (m) {
        const sign = m[1]
        const hours = parseInt(m[2], 10)
        const mins = m[3] != null ? parseInt(m[3], 10) : 0
        if (mins === 0) {
          return `(GMT ${sign}${hours})`
        }
        return `(GMT ${sign}${hours}:${String(mins).padStart(2, '0')})`
      }
      if (/^GMT$/i.test(tzPart)) {
        return '(GMT +0)'
      }
      return `(${tzPart})`
    } catch {
      return ''
    }
  }
}
